<template>
  <div
    ref="slider"
    class="radio-slider"
    :class="{ '-large': large, '-bleed': bleed }"
    :style="style"
  >
    <label
      v-for="(radio, index) in options"
      :class="{'-disabled': radio.disabled}"
      :for="`${name}-radio-${index}`"
      :key="`${name}-label-${index}`"
      :ref="`radio-${radio.value}`"
      tabindex="0"
      @keypress.enter="!radio.disabled ? $emit('change', radio.value) : () => {}"
      ><input
        type="radio"
        :name="name"
        :value="radio.value"
        :checked="radio.value === modelValue"
        :disabled="radio.disabled"
        :id="`${name}-radio-${index}`"
        @change="action"
        @click="!radio.disabled ? $emit('clicked', $event.target.value) : () => {}"
      /><span class="no-font-spacing"
        ><span>{{ radio.label }}</span>
        <span>{{ index === highlighted ? "*" : "" }}</span></span
      ></label
    >
    <div
      class="radio-slider-selector"
      :class="{ '-ready': ready }"
      :style="selectorStyle"
    />
  </div>
</template>

<script>
/**
 * @author Torbjørn
 * @version 1.0
 */
export default {
  name: 'RadioSlider',
  model: {
    prop: 'modelValue',
    event: 'change'
  },
  props: {
    modelValue: [Number, String, Boolean],
    highlighted: {
      type: Number,
      default: -1
    },
    name: {
      type: String,
      default: 'radiogroup'
    },
    options: Array,
    backgroundColor: String,
    fontColor: String,
    sliderColor: String,
    large: Boolean,
    valueAsInteger: Boolean
  },
  data () {
    return {
      ready: false,
      windowWidth: null,
      bleed: false

    };
  },

  watch: {
    selectedRadio: {
      immediate: true,
      handler () {
        if (this.bleed) {
          const wrapperStart = this.$refs.slider.scrollLeft;
          const wrapperEnd = this.windowWidth + this.$refs.slider.scrollLeft;
          const left = this.selectedRadio[0].offsetLeft;
          const right = this.selectedRadio[0].offsetLeft + this.selectedRadio[0].offsetWidth;
          if (right > wrapperEnd) {
            this.$refs.slider.scroll({ left: right - wrapperEnd, behavior: 'smooth' });
          } else if (left < wrapperStart) {
            this.$refs.slider.scroll({ left: left - wrapperStart, behavior: 'smooth' });
          }
        }
      }
    },
    options (val) {
      this.ready = false;
      if (val.length) {
        this.$nextTick(() => {
          this.ready = true;
        });
      }
    }
  },

  computed: {
    style () {
      return {
        color: this.fontColor || this.Globals.colors.black,
        backgroundColor: this.backgroundColor || this.Globals.colors.white
      };
    },
    selectorStyle () {
      const bg = this.sliderColor || this.Globals.colors.grayLight;

      if (this.selectedRadio && this.ready && this.windowWidth > 0) {
        return {
          left: `${this.selectedRadio[0].offsetLeft}px`,
          width: `${this.selectedRadio[0].offsetWidth}px`,
          backgroundColor: bg
        };
      }
      return { backgroundColor: bg };
    },
    selectedRadio () {
      if (this.ready) {
        return this.$refs[`radio-${this.modelValue}`];
      }
      return null;
    }
  },
  methods: {
    getWindowWidth () {
      setTimeout(() => {
        this.windowWidth =
        window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth;
        if (this.$refs && this.$refs.slider) {
          if (this.windowWidth <= this.$refs.slider.offsetWidth) {
            this.bleed = true;
          }
        }
      }, 100);
    },
    action (e) {
      const v = this.valueAsInteger ? parseInt(e.target.value) : e.target.value;
      this.$emit('change', v);
      this.$emit('update:modelValue', v);
    }
  },
  updated () {
    this.getWindowWidth();
  },
  mounted () {
    window.addEventListener('resize', this.getWindowWidth);
    this.getWindowWidth();
    this.$nextTick(() => {
      this.getWindowWidth();
      this.ready = true;
    });
  },
  destroyed () {
    window.removeEventListener('resize', this.getWindowWidth);
  }
};
</script>

<style lang="scss">
@import "@/scss/defaults.scss";
.radio-slider {
  position: relative;
  display: inline-flex;
  align-items: center;
  background-color: $gray-light;
  border-radius: 21px;
  height: 30px;
  padding: 2px;
  &.-bleed {
    border-radius: 0px;
    @include media-breakpoint-down(sm) {
      max-width: 100%;
      overflow-x: scroll;
      overflow-y: hidden;
      -webkit-overflow-scrolling: touch;
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
  }

  &-selector {
    opacity: 0;
    position: absolute;
    z-index: 10;
    width: 50px;
    height: 26px;
    top: 2px;
    background-color: $white;
    border-radius: 15px;
    transition: all 0.15s;
    &.-ready {
      opacity: 1;
    }
  }
  input[type="radio"] {
    display: none;
  }

  label {
    position: relative;
    z-index: 20;
    @include font-size(small);
    margin: 0;
    cursor: pointer;
    user-select: none;
    padding: 2px 12px;
    white-space: nowrap;
    @include font-spacing-responsive(body);
    &.-disabled {
      color: $gray;
      cursor: not-allowed;
    }
  }

  &.-large {
    height: 42px;
    label {
      @include font-responsive(body);
      @include font-spacing-responsive(body);
      padding: 2px 15px;
    }
    .radio-slider-selector {
      height: 38px;
      border-radius: 19px;
    }
  }
}
</style>
